import React from 'react';
import { Box, Typography, Stack, alpha, IconButton, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStationRounded';
import ContentCopyIcon from '@mui/icons-material/ContentCopyRounded';
import OpenInNewIcon from '@mui/icons-material/OpenInNewRounded';
import { IGasRequest, IStats } from '../shared/types';
import { AlertShell } from './ui/AlertShell';
import { botLongLabel, breakAfterPair } from '../utils/botNames';

interface LowGasAlertProps {
    stats: IStats;
    onCopy?: (text: string, label: string) => void;
    isAdmin?: boolean;
    recommendedAmount?: number;
}

export const LowGasAlert: React.FC<LowGasAlertProps> = ({ stats, onCopy, isAdmin = false, recommendedAmount }) => {
    if (!isAdmin) return null;

    let requests = stats.gasRequests;
    // Compatibility with monitor backends that only report the operating-chain flag.
    if (requests === undefined && stats.isLowGas) {
        const botName = stats.botName.toUpperCase();
        const chainId = botName.startsWith('RSK_') ? 30 : botName.startsWith('ARB_') ? 42161 : 1;
        const requiredBalance = recommendedAmount && recommendedAmount > 0 ? recommendedAmount : chainId === 30 ? 0.002 : 0.05;
        const currentBalance = stats.balanceSnapshotEnd?.tokenNativeBalance || 0;
        requests = [{
            botId: stats.botName,
            chainId,
            tokenSymbol: chainId === 30 ? 'RBTC' : 'ETH',
            walletAddress: stats.walletAddress || '',
            currentBalance,
            requiredBalance,
            missingAmount: Math.max(0, requiredBalance - currentBalance),
            updatedAt: stats.positionLastUpdated || 0,
            source: 'balance',
        }];
    }

    return <>{requests?.map(request => (
        <GasRequestCard key={`${request.botId}-${request.chainId}-${request.walletAddress}`} request={request} onCopy={onCopy} />
    ))}</>;
};

const GAS_NETWORKS: Record<number, { name: string; explorer: string }> = {
    1: { name: 'Ethereum', explorer: 'https://etherscan.io/address/' },
    30: { name: 'Rootstock', explorer: 'https://explorer.rootstock.io/address/' },
    42161: { name: 'Arbitrum', explorer: 'https://arbiscan.io/address/' },
};

/** "0.04269000" → "0.04269", "1.00000000" → "1". */
const trimZeros = (fixed: string): string => (fixed.includes('.') ? fixed.replace(/\.?0+$/, '') : fixed);

/** 8-decimal precision, rounded up (never shows a top-up of zero), trailing zeros trimmed: 0.05000000 → "0.05". */
const formatTopUp = (amount: number): string => trimZeros((Math.ceil(amount * 1e8) / 1e8).toFixed(8));

const GasRequestCard: React.FC<{ request: IGasRequest; onCopy: LowGasAlertProps['onCopy'] }> = ({ request, onCopy }) => {
    const theme = useTheme();
    const network = GAS_NETWORKS[request.chainId]?.name || `Chain ${request.chainId}`;
    const explorerPrefix = GAS_NETWORKS[request.chainId]?.explorer;
    const gasToken = request.tokenSymbol;
    const gasAmount = request.currentBalance;
    const walletAddress = request.walletAddress;
    const isTransaction = request.source === 'transaction';
    // Gas for a pending transaction blocks the bot (critical); a balance below
    // the recommended level only needs a top-up (requires action).
    const statusColor = isTransaction ? theme.palette.error.main : theme.palette.warning.main;

    const handleCopyAddress = () => {
        if (onCopy && walletAddress) {
            onCopy(walletAddress, 'Wallet address');
        } else if (walletAddress) {
            navigator.clipboard.writeText(walletAddress);
        }
    };

    const handleOpenExplorer = () => {
        if (walletAddress && explorerPrefix) {
            window.open(`${explorerPrefix}${walletAddress}`, '_blank', 'noopener,noreferrer');
        }
    };

    return (
        <AlertShell
            color={statusColor}
            icon={<LocalGasStationIcon />}
            title={<AlertBotTitle botId={request.botId} />}
            // Operational alerts share one subtitle pattern: "<state> · <network>", sentence case.
            subtitle={`${isTransaction ? 'Gas required' : 'Low gas balance'} · ${network}`}
            fields={[
                {
                    label: 'Current balance',
                    value: `${trimZeros(gasAmount.toFixed(8))} ${gasToken}`,
                    color: statusColor,
                },
                {
                    label: isTransaction ? 'Required (estimated)' : 'Recommended',
                    value: `${formatTopUp(request.requiredBalance)} ${gasToken}`,
                },
                {
                    label: isTransaction ? 'Estimated top-up' : 'Top-up',
                    value: `${formatTopUp(request.missingAmount)} ${gasToken}`,
                    color: statusColor,
                },
            ]}
            trailing={
                walletAddress ? (
                    <AddressPill
                        address={walletAddress}
                        onCopy={handleCopyAddress}
                        onOpenExplorer={explorerPrefix ? handleOpenExplorer : undefined}
                    />
                ) : undefined
            }
        />
    );
};

/**
 * Title of the operational alerts: the bot with its own chain ("RIF / USDT · Rootstock · Bot 2"), the raw id
 * in the tooltip. In the narrow identity column of a one-row alert it wraps after the pair instead of
 * cutting off the bot number.
 */
export const AlertBotTitle: React.FC<{ botId: string }> = ({ botId }) => (
    <Box component="span" title={botId} sx={{ whiteSpace: 'normal' }}>
        {breakAfterPair(botLongLabel(botId, { chain: true }))}
    </Box>
);

/** Shortens long addresses: "0xef0f…af58". */
export const shortAddress = (address: string): string =>
    address.length > 20 ? `${address.slice(0, 6)}…${address.slice(-4)}` : address;

interface AddressPillProps {
    address: string;
    onCopy: () => void;
    /** Shown only when the network has an explorer. */
    onOpenExplorer?: () => void;
    copyLabel?: string;
}

/** Monospace address pill with copy and (optional) open-in-explorer actions, shared by the operational alerts. */
export const AddressPill: React.FC<AddressPillProps> = ({ address, onCopy, onOpenExplorer, copyLabel = 'Copy address' }) => {
    const theme = useTheme();
    return (
        <Stack
            direction="row"
            alignItems="center"
            spacing={0.25}
            sx={{
                pl: 1.25,
                pr: 0.25,
                height: 34,
                borderRadius: 999,
                border: `1px solid ${alpha(theme.palette.text.primary, 0.12)}`,
                bgcolor: 'rgba(0,0,0,0.25)',
            }}
        >
            <Typography
                component="span"
                title={address}
                sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.75rem', color: 'text.secondary', mr: 0.5 }}
            >
                {shortAddress(address)}
            </Typography>
            {/* ~25px buttons 21px apart (10px · 1px divider · 10px): centres 46px apart, so the 44px touch
                areas don't overlap, and the hairline makes the gap between the two actions read as deliberate. */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Tooltip title={copyLabel}>
                    <IconButton size="small" onClick={onCopy} aria-label={copyLabel} sx={{ color: 'text.secondary' }}>
                        <ContentCopyIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                </Tooltip>
                {onOpenExplorer && (
                    <>
                        <Box aria-hidden sx={{ width: '1px', height: 16, flexShrink: 0, bgcolor: 'rgba(255,255,255,0.12)' }} />
                        <Tooltip title="View in explorer">
                            <IconButton size="small" onClick={onOpenExplorer} aria-label="View in explorer" sx={{ color: 'text.secondary' }}>
                                <OpenInNewIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                        </Tooltip>
                    </>
                )}
            </Box>
        </Stack>
    );
};
